import { Adapter } from './adapter';
import { LocalDataService } from 'src/packages/mitsBasics';
import { BookingServices, PendingPhase2Commit } from '../../types';
import { ModelAdapters } from '../model-adapters';

const LOCAL_STORAGE_KEY = 'bookingPendingPhase2Commits';

/**
 * Persistiert Phase-2-Marker zweiphasiger Movements (NormalMovement:
 * `createRemote` + `bookRemote`): nach Phase 1 geschrieben, nach Phase 2
 * entfernt, damit ein Netzabriss dazwischen die Movement-ID nicht verliert.
 * Der Marker überlebt einen App-Neustart und wird über `retryAll()`
 * nachgeholt; `bookRemote` ist idempotent, Mehrfach-Nachholen also unschädlich.
 */
export class PendingCommitAdapter extends Adapter {
  private readonly store: LocalDataService<PendingPhase2Commit>;

  constructor(services: BookingServices, adapters: ModelAdapters) {
    super(services, adapters);
    this.store = new LocalDataService(LOCAL_STORAGE_KEY);
  }

  /**
   * Merkt sich ein Movement mit ausstehender Phase 2. Idempotent pro
   * Movement-ID: ein Retry überschreibt den bestehenden Marker, statt ein
   * Duplikat anzulegen.
   *
   * @param movementId Remote-ID des Phase-1-Movements
   * @param clientId `client_id` des Movements (Diagnose/Korrelation)
   * @returns `false`, wenn der Marker nicht geschrieben werden konnte.
   */
  public add(movementId: number, clientId?: string): boolean {
    return !!this.store.save({ id: movementId, clientId });
  }

  /**
   * Entfernt den Marker (Phase 2 erledigt). Idempotent: liefert auch `true`,
   * wenn kein Marker existiert.
   *
   * @param movementId Remote-ID des Phase-1-Movements
   * @returns `false`, wenn ein vorhandener Marker nicht gelöscht werden konnte.
   */
  public remove(movementId: number): boolean {
    if (!this.store.find(movementId)) {
      return true;
    }
    return this.store.delete(movementId);
  }

  /**
   * Alle ausstehenden Phase-2-Marker.
   *
   * @returns Die offenen Marker, leer wenn keine vorliegen.
   */
  public all(): PendingPhase2Commit[] {
    return this.store.all() ?? [];
  }

  /**
   * Holt alle ausstehenden Phase-2-Commits nach: committete Marker werden
   * entfernt, fehlgeschlagene bleiben liegen. Lokale Bestands-Spiegel werden
   * nicht nachgezogen (der Down-Sync gleicht die Bestände ab).
   *
   * @returns Anzahl der danach weiterhin offenen Marker.
   */
  public async retryAll(): Promise<number> {
    let open = 0;
    for (const pending of this.all()) {
      const result = await this.adapters.movement.bookRemote(pending.id);
      // alreadyBooked (409): Phase 2 war real schon durch, Ziel erreicht -
      // Marker entfernen statt ewig zu retrien.
      if (result.booked || result.alreadyBooked) {
        this.remove(pending.id);
      } else {
        open++;
      }
    }
    return open;
  }
}
